import { h } from '@atomify/jsx';
import { Button, ButtonA } from '@source/components/atoms/button/button';
import { Image } from '@source/components/atoms/image';
import { COLOR_CLASSES, FormattedPrice, FormattedStatus } from '@source/utilities/api-formatters';

interface HouseSelectorCardProps {
    title: string;
    image?: string | null;
    description?: string | null;
    identifier?: string | null;
    status?: FormattedStatus | null;
    price?: FormattedPrice | null;
    specs?:
        | {
              title: string | Text;
              subtitle: string | Text;
          }[]
        | null;
    buttonLabel?: string | Text | null;
    buttonUrl?: string | null;
    onButtonClick?: (event: MouseEvent) => void;
    onClose?: Function | null;
    isNhg?: boolean;
}

const HouseSelectorCard = ({
    image,
    title,
    description,
    identifier,
    status,
    price,
    specs,
    isNhg,
    buttonLabel,
    buttonUrl,
    onButtonClick,
    onClose,
}: HouseSelectorCardProps) => {
    return (
        <div class="c-house-selector-card">
            {image && (
                <div class="house-selector-card__image-container">
                    <Image
                        classes="house-selector-card__image"
                        preload={{
                            width: 310,
                            height: 200,
                        }}
                        settings={{
                            '640w': {
                                width: 640,
                                height: 360,
                            },
                        }}
                        image={image}
                        alt={title}
                        objectFit={true}
                    />
                </div>
            )}

            {identifier && (
                <span class="house-selector-card__object-identifier">#{identifier}</span>
            )}

            {onClose && (
                <Button
                    classes="house-selector-card__close"
                    icon="icons-close"
                    variant="white"
                    width="square"
                    shape="round"
                    size="small"
                    onClick={onClose}
                />
            )}

            <div class="house-selector-card__content">
                <div
                    class="house-selector-card__main-content"
                    js-hook-house-selector-card-main-content>
                    {status && (
                        <span
                            class={`house-selector-card__status u-body-normal-bold ${
                                COLOR_CLASSES[status.color]
                            }`}>
                            {status.label}
                        </span>
                    )}

                    <p class="house-selector-card__title u-body-large-bold">{title}</p>

                    {price && (
                        <span class="house-selector-card__price">
                            {price.prefix && (
                                <span class="u-status-large u-color--grey-30 u-margin-right-1">
                                    {price.prefix}
                                </span>
                            )}
                            {price.amount && price.amount}
                            {price.suffix && (
                                <span class="u-status-large u-color--grey-30 u-margin-left-1">
                                    {price.suffix}
                                </span>
                            )}
                            {isNhg && (
                                <span class="u-margin-left-1 u-color--positive u-label">NHG</span>
                            )}
                        </span>
                    )}

                    {description && <p class="house-selector-card__description">{description}</p>}
                </div>

                {specs && (
                    <div class="house-selector-card__specs u-margin-top-2">
                        {specs.map((spec) => (
                            <div class="house-selector-card__spec u-background--bg">
                                <p class="u-body-normal-bold u-color--grey-80">{spec.title}</p>
                                <span class="u-body-normal">{spec.subtitle}</span>
                            </div>
                        ))}
                    </div>
                )}

                {buttonLabel && buttonUrl && (
                    <ButtonA
                        classes="u-margin-top-2"
                        width="full-width"
                        label={buttonLabel}
                        url={buttonUrl}
                        onClick={onButtonClick}
                    />
                )}

                {buttonLabel && !buttonUrl && onButtonClick && (
                    <Button
                        classes="u-margin-top-2"
                        width="full-width"
                        label={buttonLabel}
                        onClick={onButtonClick}
                    />
                )}
            </div>
        </div>
    );
};

export { HouseSelectorCard };
